import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../ui/dialog.tsx";
import { Label } from "../../ui/label.tsx";
import { Input } from "../../ui/input.tsx";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select.tsx";
import { Button } from "../../ui/button.tsx";
import { Loader2 } from "lucide-react";
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useEffect } from 'react';
import { toast } from 'sonner';
// Plan type from usePartnerSubAccountCreation hook
interface AvailablePlan {
  id: string;
  name: string;
  identifier: string;
  price: number;
  billing_period: string;
  features: string[];
  is_custom: boolean;
}
import { useUpdateSubAccount } from "../../../hooks/useSubAccounts.ts";

// Form validation schema
const updateSettingsSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  plan: z.string().min(1, 'Plan is required'),
});

type UpdateSettingsFormData = z.infer<typeof updateSettingsSchema>;

interface Account {
  user_id: string;
  full_name: string;
  email: string;
  subscription_plan_identifier: string;
}

interface SettingsDialogProps {
  isopen: boolean;
  setIsOpen: (open: boolean) => void;
  account: Account | null;
  availablePlans: AvailablePlan[];
}

function SettingsDialog({ isopen, setIsOpen, account, availablePlans }: SettingsDialogProps) {
  const updateSubAccountMutation = useUpdateSubAccount();


  const { register, handleSubmit, formState: { errors, isSubmitting }, reset, setValue, watch } = useForm<UpdateSettingsFormData>({
    resolver: zodResolver(updateSettingsSchema),
    defaultValues: { name: '', plan: '' }
  });

  useEffect(() => {
    if (account) {
      setValue('name', account.full_name || '');
      setValue('plan', account.subscription_plan_identifier || '');
    }
  }, [account, setValue]);

  const onSubmit = async (data: UpdateSettingsFormData) => {
    if (!account) return;
    try {
      await updateSubAccountMutation.mutateAsync({
        user_id: account.user_id,
        name: data.name,
        plan: data.plan,
      });
      
      toast.success('Account settings updated successfully');
      setIsOpen(false);
      reset();
    } catch (error) {
      toast.error(`Failed to update settings: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  if (!account) return null;

  return (
    <Dialog open={isopen} onOpenChange={setIsOpen}> 
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Account Settings</DialogTitle>
          <DialogDescription>
            Update settings for {account.email}
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 py-4">
          <div>
            <Label htmlFor="name">Customer Name</Label>
            <Input 
              id="name"
              {...register('name')}
              placeholder="John Doe"
              className={errors.name ? 'border-red-500' : ''}
            />
            {errors.name && (<p className="text-sm text-red-500 mt-1">{errors.name.message}</p>)}
          </div>
          
          <div>
            <Label htmlFor="plan">Plan</Label>
            <Select 
              value={watch('plan')} 
              onValueChange={(value: string) => setValue('plan', value)}
            >
              <SelectTrigger className={errors.plan ? 'border-red-500' : ''}>
                <SelectValue placeholder="Select a plan" />
              </SelectTrigger>
              <SelectContent>
                {availablePlans.map((plan) => (
                  <SelectItem key={plan.id} value={plan.identifier}>
                    {plan.name} - ${plan.price}/{plan.billing_period}
                  </SelectItem>

                ))}
              </SelectContent>
            </Select>
            {errors.plan && ( <p className="text-sm text-red-500 mt-1">{errors.plan.message}</p>)}
          </div>
          
          <div className="flex justify-end space-x-2 pt-4">
            <Button variant="outline" type="button" onClick={() => setIsOpen(false)}>
              Cancel
            </Button>
            <Button 
              type="submit"
              disabled={isSubmitting || updateSubAccountMutation.isPending}
            >
              {isSubmitting || updateSubAccountMutation.isPending ? (
                <><Loader2 className="size-4 mr-2 animate-spin" />Updating...</>
              ) : (
                'Update Settings'
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default SettingsDialog;
export type { UpdateSettingsFormData, Account, SettingsDialogProps };